import { useState, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { Run, PassId, Provider, KnowledgeUnit } from '../types'

export interface RunPayload {
  standardId: string
  standardText: string
  subject: string
  grade: string | number
  passId: PassId
  provider: Provider
  model: string
  promptText: string
  slot: 'extract' | 'generate'
  promptLabel: string
  sourceDoc?: string
  kuCount?: number
  experimentId?: string
  conditionId?: string
  systemPrompt?: string
}

export function useRuns(userId: string) {
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRuns = useCallback(async (standardId: string, passId?: PassId): Promise<Run[]> => {
    let query = supabase
      .from('runs')
      .select('*')
      .eq('standard_id', standardId)
      .order('created_at', { ascending: false })
    if (passId) query = query.eq('pass_id', passId)
    const { data } = await query
    return (data as Run[]) || []
  }, [])

  const executeRun = useCallback(
    async (payload: RunPayload): Promise<{ run: Run; kus: KnowledgeUnit[]; sourceDoc?: string }> => {
      setRunning(true)
      setError(null)
      try {
        // Create run record
        const { data: runData, error: runErr } = await supabase
          .from('runs')
          .insert({
            user_id: userId,
            standard_id: payload.standardId,
            pass_id: payload.passId,
            provider: payload.provider,
            model: payload.model,
            prompt_slot: payload.slot,
            prompt_label: payload.promptLabel,
            prompt_text: payload.promptText,
            source_doc: payload.sourceDoc || null,
            ku_count_target: payload.kuCount || 5,
            experiment_id: payload.experimentId || null,
            condition_id: payload.conditionId || null,
            status: 'running',
          })
          .select()
          .single()

        if (runErr || !runData) throw new Error(runErr?.message || 'Failed to create run')
        const run = runData as Run

        // Call Edge Function
        const { data: fnData, error: fnErr } = await supabase.functions.invoke('extract-kos', {
          body: {
            runId: run.id,
            standardId: payload.standardId,
            standardText: payload.standardText,
            subject: payload.subject,
            grade: payload.grade,
            passId: payload.passId,
            provider: payload.provider,
            model: payload.model,
            promptText: payload.promptText,
            slot: payload.slot,
            sourceDoc: payload.sourceDoc,
            kuCount: payload.kuCount || 5,
            experimentId: payload.experimentId,
            conditionId: payload.conditionId,
            systemPrompt: payload.systemPrompt,
          },
        })

        if (fnErr) {
          // Try to extract detailed error from response body
          let msg = fnErr.message
          try {
            const errBody = await (fnErr as unknown as { context?: { text?: () => Promise<string> } }).context?.text?.()
            if (errBody) {
              const parsed = JSON.parse(errBody)
              msg = parsed.error || parsed.message || errBody
            }
          } catch {}
          throw new Error(msg)
        }

        const kus = (fnData?.kus as KnowledgeUnit[]) || []
        const sourceDoc = fnData?.sourceDoc as string | undefined
        return { run: { ...run, status: 'complete' }, kus, sourceDoc }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Run failed'
        setError(msg)
        throw new Error(msg)
      } finally {
        setRunning(false)
      }
    },
    [userId],
  )

  return { executeRun, fetchRuns, running, error }
}
